import { useState } from "react";
import { Brain, Repeat } from "lucide-react";
import AudioMessage from "@/components/chat/AudioMessage";
import MediaMessage from "@/components/chat/MediaMessage";
import { formatMessageTime } from "@/utils/messageUtils";
import { formatWhatsAppText } from "@/lib/whatsappTextFormat";
import type { Database } from "@/lib/supabase";

type AccountMessage = Database["public"]["Tables"]["account_messages"]["Row"];

export interface MobileMessage extends Omit<AccountMessage, "id"> {
  id: string | number;
  isOptimistic?: boolean;
  isSending?: boolean;
  sendError?: string;
  account_contact_id?: string | null;
  processed_media_url?: string | null;
}

interface MobileMessageBubbleProps {
  message: MobileMessage;
}

/**
 * Bolha de mensagem otimizada para telas pequenas. Reaproveita os componentes
 * de mídia do chat web (AudioMessage/MediaMessage) e a formatação de texto.
 */
export const MobileMessageBubble = ({ message }: MobileMessageBubbleProps) => {
  const [localTranscription, setLocalTranscription] = useState<string | null>(
    (message as { transcription?: string | null }).transcription || null
  );

  const fromMe = message.from_me === true;
  const isAudio = message.media_kind === "audioMessage" || message.media_kind === "audio";
  const isImage = message.media_kind === "imageMessage" || message.media_kind === "image";
  const isVideo = message.media_kind === "videoMessage" || message.media_kind === "video";
  const isDocument = message.media_kind === "documentMessage" || message.media_kind === "document";
  const hasMedia = isAudio || isImage || isVideo || isDocument;
  const isAiMessage = fromMe && message.is_from_ai;
  const isFollowupMessage = fromMe && (message as { is_followup?: boolean }).is_followup === true;

  const messageId = typeof message.id === "string" ? message.id : String(message.id);
  const messageTime = formatMessageTime(message.sent_at);

  // Mesma prioridade de URL de mídia usada no chat web
  const mediaUrl = message.media_url;
  const isWhatsAppMedia =
    typeof mediaUrl === "string" && mediaUrl.includes("mmg.whatsapp.net");
  const isSupabaseStorage =
    typeof mediaUrl === "string" &&
    (mediaUrl.includes("supabase.co/storage") || mediaUrl.includes("/storage/v1/object/public/"));
  const rawPayload = message.raw_payload as Record<string, unknown> | null;
  const isSentDocument =
    fromMe && rawPayload?.sent_via_dashboard === true && rawPayload?.storage_bucket === "sent-documents";

  const effectiveMediaUrl =
    message.processed_media_url ||
    (isSentDocument ? mediaUrl : null) ||
    (isSupabaseStorage ? mediaUrl : null) ||
    (isWhatsAppMedia ? mediaUrl : null);

  const bubbleBackground = () => {
    if (!fromMe) {
      return "bg-white border border-border shadow-sm dark:bg-neutral-600 dark:border-transparent dark:shadow-none";
    }
    if (isAiMessage) {
      return "bg-blue-50 border border-blue-100 dark:bg-[#1a365d] dark:border-transparent";
    }
    if (isFollowupMessage) {
      return "bg-violet-50 border border-violet-100 dark:bg-[#3b2f5e] dark:border-transparent";
    }
    return "bg-[#DCF8C6] border border-[#c5e8b0] shadow-sm dark:bg-[#005C4B] dark:border-transparent dark:shadow-none";
  };

  return (
    <div className={`flex ${fromMe ? "justify-end" : "justify-start"}`}>
      <div
        className={`relative max-w-[82%] rounded-2xl px-3 py-2 text-foreground dark:text-neutral-50 ${bubbleBackground()} ${
          fromMe ? "rounded-br-sm" : "rounded-bl-sm"
        } ${message.isOptimistic && message.isSending ? "opacity-70" : ""} ${
          message.sendError ? "border border-red-500/50" : ""
        }`}
      >
        {message.isOptimistic && message.isSending && (
          <div className="absolute -right-1 -top-1 h-3 w-3">
            <div className="h-3 w-3 animate-spin rounded-full border-2 border-neutral-300 border-t-transparent" />
          </div>
        )}
        {message.sendError && (
          <div className="absolute -right-1 -top-1 flex h-3 w-3 items-center justify-center rounded-full bg-red-500">
            <span className="text-xs text-white">!</span>
          </div>
        )}

        {message.text_content && !hasMedia && (
          <p className="whitespace-pre-wrap break-words text-[14px] leading-snug">
            {formatWhatsAppText(message.text_content)}
          </p>
        )}

        {isAudio && message.wa_message_id && (
          <AudioMessage
            messageId={messageId}
            waMessageId={message.wa_message_id}
            existingProcessedUrl={effectiveMediaUrl}
            transcription={localTranscription}
            onTranscriptionProcessed={(t) => setLocalTranscription(t)}
            isSending={message.isSending}
          />
        )}

        {isImage && message.wa_message_id && (
          <MediaMessage
            messageId={messageId}
            waMessageId={message.wa_message_id}
            mediaType="image"
            existingProcessedUrl={effectiveMediaUrl}
          />
        )}

        {isVideo && message.wa_message_id && (
          <MediaMessage
            messageId={messageId}
            waMessageId={message.wa_message_id}
            mediaType="video"
            existingProcessedUrl={effectiveMediaUrl}
          />
        )}

        {isDocument && message.wa_message_id && (
          <MediaMessage
            messageId={messageId}
            waMessageId={message.wa_message_id}
            mediaType="document"
            existingProcessedUrl={effectiveMediaUrl}
            isSending={message.isSending}
            fileName={rawPayload?.fileName as string | undefined}
          />
        )}

        {message.text_content && hasMedia && (
          <p className="mt-2 whitespace-pre-wrap break-words text-[14px] leading-snug">
            {formatWhatsAppText(message.text_content)}
          </p>
        )}

        <div className="mt-1 flex items-center justify-end gap-1.5">
          {isAiMessage && (
            <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-1.5 py-0.5 text-primary dark:bg-[#1a365d]/50 dark:text-white/90">
              <Brain className="h-2.5 w-2.5" />
              <span className="text-[9px] font-medium">IA</span>
            </span>
          )}
          {isFollowupMessage && !isAiMessage && (
            <span className="inline-flex items-center gap-1 rounded-full bg-violet-100 px-1.5 py-0.5 text-violet-700 dark:bg-[#3b2f5e]/60 dark:text-white/90">
              <Repeat className="h-2.5 w-2.5" />
              <span className="text-[9px] font-medium">Follow-up</span>
            </span>
          )}
          <span
            className={`text-[10px] ${
              message.sendError
                ? "text-red-500 dark:text-red-300"
                : "text-muted-foreground dark:text-neutral-300"
            }`}
          >
            {message.sendError ? "Erro ao enviar" : messageTime}
          </span>
        </div>
      </div>
    </div>
  );
};
